/**
 * Serializes a slide's animations to PresentationML <p:timing> and parses it back.
 *
 * Neither PptxGenJS nor pptx-parser touches animation XML, so both directions are written
 * here. Export runs after PptxGenJS has produced the file: shapes are tagged with an
 * `objectName` token, the token is matched back to the shape id PptxGenJS assigned, and a
 * <p:timing> block is injected into the slide part.
 *
 * Structure follows PowerPoint's own output: a tmRoot par -> mainSeq -> one par per click
 * group -> one par per effect. Trigger semantics (with/after previous) are flattened into
 * absolute delays, which our scheduler has already computed and which PowerPoint reads back
 * as the equivalent timing.
 */

import { buildTimeline, normalizeAnimations } from './animationUtils';

/** Token written into each shape's objectName so it can be found in the generated XML. */
export const ANIM_TOKEN_PREFIX = 'msanim';

export const animObjectName = (targetId, subTargetId = null) =>
  subTargetId
    ? `${ANIM_TOKEN_PREFIX}|${targetId}|${subTargetId}`
    : `${ANIM_TOKEN_PREFIX}|${targetId}`;

/**
 * PowerPoint preset identifiers per effect. presetClass 'entr' marks an entrance.
 * The visible result is produced by the animEffect filter below; presetID/presetSubtype
 * exist so PowerPoint's Animations UI shows the matching gallery item when reopened.
 */
const PRESETS = {
  appear: { id: 1, subtype: 0 },
  fade: { id: 10, subtype: 0 },
  flyIn: { id: 2, subtype: 4 },
  wipe: { id: 22, subtype: 4 },
  zoom: { id: 23, subtype: 16 },
  split: { id: 13, subtype: 26 },
  box: { id: 11, subtype: 12 },
  circle: { id: 12, subtype: 16 },
  stretch: { id: 51, subtype: 8 },
  growTurn: { id: 31, subtype: 0 },
  floatIn: { id: 42, subtype: 4 },
  bounce: { id: 26, subtype: 0 },
  swivel: { id: 18, subtype: 10 },
};

/** Directional presetSubtype values are the standard OOXML octants. */
const DIRECTION_SUBTYPE = {
  fromTop: 1,
  fromRight: 2,
  fromBottom: 4,
  fromLeft: 8,
};

const SPLIT_SUBTYPE = {
  horizontalOut: 26,
  horizontalIn: 25,
  verticalOut: 22,
  verticalIn: 21,
};

const BOX_SUBTYPE = { in: 16, out: 12 };
const SWIVEL_SUBTYPE = { horizontal: 10, vertical: 5 };
const STRETCH_SUBTYPE = {
  fromLeft: 8, fromRight: 2, fromTop: 1, fromBottom: 4, fromCenter: 16,
};

/** Which subtype table an effect's direction is encoded with. */
const SUBTYPE_TABLE = {
  split: SPLIT_SUBTYPE,
  box: BOX_SUBTYPE,
  swivel: SWIVEL_SUBTYPE,
  stretch: STRETCH_SUBTYPE,
  flyIn: DIRECTION_SUBTYPE,
  wipe: DIRECTION_SUBTYPE,
  floatIn: DIRECTION_SUBTYPE,
};

/**
 * The animEffect transition filter that renders the effect on open.
 *
 * Where PowerPoint has a matching transition filter, that string is used and the effect
 * plays as authored. Bounce, Grow & Turn and Swivel have no filter equivalent — PowerPoint
 * builds them from animScale/animRot behaviour trees rather than a transition — so they
 * degrade to the nearest available filter. The object still enters at the right moment with
 * the right timing, and presetID keeps the gallery entry correct, but the exported motion is
 * an approximation of what plays in the editor.
 */
const FILTERS = {
  fade: () => 'fade',
  zoom: () => 'zoom(in)',
  circle: () => 'circle(in)',
  wipe: (dir) => ({
    fromBottom: 'wipe(up)',
    fromTop: 'wipe(down)',
    fromLeft: 'wipe(right)',
    fromRight: 'wipe(left)',
  }[dir] || 'wipe(up)'),
  flyIn: (dir) => ({
    fromBottom: 'slide(fromBottom)',
    fromTop: 'slide(fromTop)',
    fromLeft: 'slide(fromLeft)',
    fromRight: 'slide(fromRight)',
  }[dir] || 'slide(fromBottom)'),
  floatIn: (dir) => (dir === 'fromTop' ? 'slide(fromTop)' : 'slide(fromBottom)'),
  split: (dir) => ({
    horizontalOut: 'barn(outHorizontal)',
    horizontalIn: 'barn(inHorizontal)',
    verticalOut: 'barn(outVertical)',
    verticalIn: 'barn(inVertical)',
  }[dir] || 'barn(outHorizontal)'),
  box: (dir) => (dir === 'in' ? 'box(in)' : 'box(out)'),
  stretch: () => 'stretch(across)',
  // No transition-filter equivalent; nearest visual match.
  growTurn: () => 'zoom(in)',
  bounce: () => 'slide(fromTop)',
  swivel: () => 'fade',
};

const presetFor = (step) => {
  const base = PRESETS[step.effect] || PRESETS.fade;
  const table = SUBTYPE_TABLE[step.effect];
  if (table) return { id: base.id, subtype: table[step.direction] ?? base.subtype };
  return base;
};

/** Monotonic id source; every cTn in a slide's timing tree needs a unique id. */
const idGenerator = (start = 1) => {
  let next = start;
  return () => next++;
};

/**
 * One effect: a visibility `set` (so the shape starts hidden and appears on cue) plus,
 * for everything except Appear, the animEffect that renders the transition.
 */
const effectXml = (step, spid, nextId) => {
  const preset = presetFor(step);
  const cTnId = nextId();
  const setId = nextId();
  const delay = Math.max(0, Math.round(step.startOffset || 0));

  const setNode =
    `<p:set>` +
      `<p:cBhvr>` +
        `<p:cTn id="${setId}" dur="1" fill="hold">` +
          `<p:stCondLst><p:cond delay="0"/></p:stCondLst>` +
        `</p:cTn>` +
        `<p:tgtEl><p:spTgt spid="${spid}"/></p:tgtEl>` +
        `<p:attrNameLst><p:attrName>style.visibility</p:attrName></p:attrNameLst>` +
      `</p:cBhvr>` +
      `<p:to><p:strVal val="visible"/></p:to>` +
    `</p:set>`;

  let animNode = '';
  if (step.effect !== 'appear') {
    const filterFor = FILTERS[step.effect] || FILTERS.fade;
    const animId = nextId();
    animNode =
      `<p:animEffect transition="in" filter="${filterFor(step.direction)}">` +
        `<p:cBhvr>` +
          `<p:cTn id="${animId}" dur="${Math.max(1, Math.round(step.duration))}"/>` +
          `<p:tgtEl><p:spTgt spid="${spid}"/></p:tgtEl>` +
        `</p:cBhvr>` +
      `</p:animEffect>`;
  }

  return (
    `<p:par>` +
      `<p:cTn id="${cTnId}" presetID="${preset.id}" presetClass="entr" ` +
        `presetSubtype="${preset.subtype}" fill="hold" nodeType="${step.__first ? 'clickEffect' : 'withEffect'}">` +
        `<p:stCondLst><p:cond delay="${delay}"/></p:stCondLst>` +
        `<p:childTnLst>${setNode}${animNode}</p:childTnLst>` +
      `</p:cTn>` +
    `</p:par>`
  );
};

/**
 * One click group: waits for a click, then runs its effects at their scheduled offsets.
 *
 * `resolveSpids` returns every shape a step covers. That is one shape for most objects, but
 * a SmartArt animated "as one object" flattens to many shapes on export, so they all
 * animate together at the same offset.
 */
const groupXml = (group, resolveSpids, nextId) => {
  const effects = group.steps
    .map((step, index) =>
      (resolveSpids(step) || [])
        .map((spid, spidIndex) =>
          effectXml({ ...step, __first: index === 0 && spidIndex === 0 }, spid, nextId)
        )
        .join('')
    )
    .join('');
  if (!effects) return ''; // no targeted shape survived into the exported file

  const outerId = nextId();
  const innerId = nextId();
  return (
    `<p:par>` +
      `<p:cTn id="${outerId}" fill="hold">` +
        `<p:stCondLst><p:cond delay="indefinite"/></p:stCondLst>` +
        `<p:childTnLst>` +
          `<p:par>` +
            `<p:cTn id="${innerId}" fill="hold">` +
              `<p:stCondLst><p:cond delay="0"/></p:stCondLst>` +
              `<p:childTnLst>${effects}</p:childTnLst>` +
            `</p:cTn>` +
          `</p:par>` +
        `</p:childTnLst>` +
      `</p:cTn>` +
    `</p:par>`
  );
};

/**
 * Build the <p:timing> element for one slide.
 *
 * @param {Object} animations  the slide's animations container
 * @param {Object} slideData   the slide, for resolving targets and SmartArt nodes
 * @param {Function} resolveSpids  (step) => array of shape ids in the exported XML
 * @returns {string} the <p:timing> element, or '' when there is nothing to emit
 */
export const buildTimingXml = (animations, slideData, resolveSpids) => {
  const timeline = buildTimeline(animations, slideData);
  const { passthrough } = normalizeAnimations(animations);

  const nextId = idGenerator(3); // 1 = tmRoot, 2 = mainSeq
  const groups = timeline.map((group) => groupXml(group, resolveSpids, nextId)).join('');
  const preserved = passthrough.join('');

  if (!groups && !preserved) return '';

  return (
    `<p:timing>` +
      `<p:tnLst>` +
        `<p:par>` +
          `<p:cTn id="1" dur="indefinite" restart="never" nodeType="tmRoot">` +
            `<p:childTnLst>` +
              `<p:seq concurrent="1" nextAc="seek">` +
                `<p:cTn id="2" dur="indefinite" nodeType="mainSeq">` +
                  `<p:childTnLst>${groups}${preserved}</p:childTnLst>` +
                `</p:cTn>` +
                `<p:prevCondLst>` +
                  `<p:cond evt="onPrev" delay="0"><p:tgtEl><p:sldTgt/></p:tgtEl></p:cond>` +
                `</p:prevCondLst>` +
                `<p:nextCondLst>` +
                  `<p:cond evt="onNext" delay="0"><p:tgtEl><p:sldTgt/></p:tgtEl></p:cond>` +
                `</p:nextCondLst>` +
              `</p:seq>` +
            `</p:childTnLst>` +
          `</p:cTn>` +
        `</p:par>` +
      `</p:tnLst>` +
    `</p:timing>`
  );
};

/**
 * Map objectName tokens in a slide's XML to the shape ids PptxGenJS assigned.
 * Returns { 'msanim|target|node': '5', ... }.
 */
export const mapShapeIds = (slideXml) => {
  const map = {};
  const pattern = new RegExp(
    `<p:cNvPr[^>]*\\bid="(\\d+)"[^>]*\\bname="(${ANIM_TOKEN_PREFIX}\\|[^"]*)"`,
    'g'
  );
  let match = pattern.exec(slideXml);
  while (match) {
    map[match[2]] = match[1];
    match = pattern.exec(slideXml);
  }
  return map;
};

/**
 * Insert (or replace) a slide part's <p:timing>. It is the last child of <p:sld>, so
 * appending before the closing tag satisfies the schema's element order.
 */
export const injectTiming = (slideXml, timingXml) => {
  if (!timingXml) return slideXml;
  const withoutExisting = slideXml.replace(/<p:timing>[\s\S]*?<\/p:timing>/, '');
  return withoutExisting.replace(/<\/p:sld>\s*$/, `${timingXml}</p:sld>`);
};

/* ------------------------------------------------------------------ *
 * Import
 * ------------------------------------------------------------------ */

/**
 * Reverse of PRESETS, for recognizing effects on the way in. Every effect uses a distinct
 * presetID precisely so this mapping is unambiguous and our own round-trip is lossless.
 */
const EFFECT_BY_PRESET_ID = Object.entries(PRESETS).reduce((acc, [effect, { id }]) => {
  acc[id] = effect;
  return acc;
}, {});

/** Reverse of the per-effect subtype tables, so direction round-trips too. */
const invert = (table) => Object.entries(table).reduce((acc, [dir, code]) => {
  if (!(code in acc)) acc[code] = dir;
  return acc;
}, {});

const DIRECTION_BY_SUBTYPE = invert(DIRECTION_SUBTYPE);
const SUBTYPE_TABLE_REVERSE = Object.entries(SUBTYPE_TABLE).reduce((acc, [effect, table]) => {
  acc[effect] = invert(table);
  return acc;
}, {});

/** Direction an effect's presetSubtype encodes, or undefined when it takes none. */
const directionFor = (effect, subtype) => {
  const table = SUBTYPE_TABLE_REVERSE[effect];
  return table ? table[subtype] : undefined;
};

const firstNumber = (value, fallback) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * Extract the animation steps a slide's <p:timing> describes.
 *
 * Only entrance effects are recognized. Anything else — emphasis, exit, motion paths,
 * media triggers — is returned as verbatim XML in `passthrough`, so a caller can preserve
 * it rather than silently discarding a deck's animation work.
 *
 * Steps carry `sourceSpid` (the shape id in the source file) rather than a target id: the
 * caller owns the mapping from source shapes to its own objects.
 *
 * @param {string} slideXml  the slide part's XML
 * @returns {{ steps: Array, passthrough: string[] }}
 */
export const parseTimingXml = (slideXml) => {
  const empty = { steps: [], passthrough: [] };
  if (!slideXml || slideXml.indexOf('<p:timing') === -1) return empty;

  const doc = new DOMParser().parseFromString(slideXml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) return empty;

  const seqs = doc.getElementsByTagName('p:seq');
  if (!seqs.length) return empty;

  // The main sequence holds the click groups; other seqs are interactive triggers.
  let mainSeq = null;
  for (let i = 0; i < seqs.length; i += 1) {
    const cTn = seqs[i].getElementsByTagName('p:cTn')[0];
    if (cTn && cTn.getAttribute('nodeType') === 'mainSeq') { mainSeq = seqs[i]; break; }
  }
  if (!mainSeq) return empty;

  const steps = [];
  const passthrough = [];
  const serializer = new XMLSerializer();

  // Every cTn carrying a presetClass is one effect, wherever it sits in the nesting.
  const allCTn = mainSeq.getElementsByTagName('p:cTn');
  for (let i = 0; i < allCTn.length; i += 1) {
    const cTn = allCTn[i];
    const presetClass = cTn.getAttribute('presetClass');
    if (!presetClass) continue;

    const presetId = firstNumber(cTn.getAttribute('presetID'), -1);
    const effect = presetClass === 'entr' ? EFFECT_BY_PRESET_ID[presetId] : null;

    const spTgt = cTn.getElementsByTagName('p:spTgt')[0];
    const spid = spTgt ? spTgt.getAttribute('spid') : null;

    if (!effect || !spid) {
      // Not something we model; keep the whole effect node so it can be re-emitted.
      passthrough.push(serializer.serializeToString(cTn.parentNode || cTn));
      continue;
    }

    const subtype = firstNumber(cTn.getAttribute('presetSubtype'), 0);
    const direction = directionFor(effect, subtype);

    const cond = cTn.getElementsByTagName('p:cond')[0];
    const delay = cond ? firstNumber(cond.getAttribute('delay'), 0) : 0;

    // Duration lives on the animEffect's own cTn, not the effect node.
    const animEffect = cTn.getElementsByTagName('p:animEffect')[0];
    const animCTn = animEffect ? animEffect.getElementsByTagName('p:cTn')[0] : null;
    const duration = animCTn ? firstNumber(animCTn.getAttribute('dur'), 500) : 500;

    steps.push({
      sourceSpid: spid,
      effect,
      ...(direction ? { direction } : {}),
      trigger: cTn.getAttribute('nodeType') === 'withEffect' ? 'withPrevious' : 'onClick',
      duration: Math.max(1, duration),
      delay: Math.max(0, delay),
    });
  }

  return { steps, passthrough };
};
